import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ChevronDown, ChevronRight, Loader2, UserPlus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { COUNTRIES, US_STATES } from "@/lib/options";
import { createCompany } from "@/api/companies";
import { createContact } from "@/api/contacts";
import { SectorCombobox } from "./SectorCombobox";
import type { Company, PipelineStatus } from "@/api/types";

const NONE = "__none__";

const companySchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  domain: z.string().nullable(),
  sector: z.string().nullable(),
  headcount: z
    .union([z.literal(""), z.string()])
    .nullable()
    .transform((v) => (v == null || v === "" ? null : Number(v)))
    .pipe(z.number().int().min(0).max(10_000_000).nullable()),
  city: z.string().nullable(),
  state: z.string().nullable(),
  country: z.string().min(2, "Pick a country"),
  pipeline_status: z.string().min(1),
  notes: z.string().nullable(),
});
type CompanyValues = z.input<typeof companySchema>;
type CompanyParsed = z.output<typeof companySchema>;

const contactSchema = z.object({
  full_name: z.string().nullable(),
  title: z.string().nullable(),
  email: z
    .union([z.literal(""), z.string().email("Invalid email")])
    .nullable(),
  phone: z.string().nullable(),
  linkedin_url: z
    .union([z.literal(""), z.string().url("Invalid URL")])
    .nullable(),
  is_decision_maker: z.boolean(),
});
type ContactValues = z.infer<typeof contactSchema>;

interface CreateCompanyDialogProps {
  projectSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pipelineStatuses: PipelineStatus[];
  /** Default pipeline_status selected on open. */
  defaultStatus?: string;
  onCreated: (company: Company) => void;
}

export function CreateCompanyDialog({
  projectSlug,
  open,
  onOpenChange,
  pipelineStatuses,
  defaultStatus,
  onCreated,
}: CreateCompanyDialogProps) {
  const [contactEnabled, setContactEnabled] = useState(true);
  const [contactExpanded, setContactExpanded] = useState(true);

  const company = useForm<CompanyValues>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: "",
      domain: null,
      sector: null,
      headcount: null,
      city: null,
      state: null,
      country: "US",
      pipeline_status: defaultStatus ?? "new",
      notes: null,
    },
  });

  const contact = useForm<ContactValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      full_name: null,
      title: null,
      email: null,
      phone: null,
      linkedin_url: null,
      is_decision_maker: true,
    },
  });

  // Reset on open with the right default status (Prospects → "new",
  // Clients → "won").
  useEffect(() => {
    if (!open) return;
    company.reset({
      name: "",
      domain: null,
      sector: null,
      headcount: null,
      city: null,
      state: null,
      country: "US",
      pipeline_status: defaultStatus ?? "new",
      notes: null,
    });
    contact.reset({
      full_name: null,
      title: null,
      email: null,
      phone: null,
      linkedin_url: null,
      is_decision_maker: true,
    });
    setContactEnabled(true);
    setContactExpanded(true);
  }, [open, defaultStatus, company, contact]);

  const country = company.watch("country");
  const showStateSelect = country === "US";

  // Clear the state when country switches off US.
  useEffect(() => {
    if (!showStateSelect) {
      company.setValue("state", null);
    }
  }, [showStateSelect, company]);

  async function onSubmit() {
    // Validate company first.
    const companyValid = await company.trigger();
    if (!companyValid) return;
    // If contact section is enabled and any contact field is filled,
    // require a name and validate.
    let parsedContact: ContactValues | null = null;
    if (contactEnabled) {
      const v = contact.getValues();
      const anyFilled =
        !!emptyToNull(v.full_name) ||
        !!emptyToNull(v.title) ||
        !!emptyToNull(v.email) ||
        !!emptyToNull(v.phone) ||
        !!emptyToNull(v.linkedin_url);
      if (anyFilled) {
        if (!emptyToNull(v.full_name)) {
          contact.setError("full_name", {
            message: "Required when adding a contact",
          });
          setContactExpanded(true);
          return;
        }
        const ok = await contact.trigger();
        if (!ok) {
          setContactExpanded(true);
          return;
        }
        parsedContact = v;
      }
    }

    const c = company.getValues() as unknown as CompanyParsed;

    try {
      const created = await createCompany(projectSlug, {
        name: c.name.trim(),
        domain: emptyToNull(c.domain),
        sector: c.sector,
        headcount: c.headcount,
        city: emptyToNull(c.city),
        state: emptyToNull(c.state),
        country: c.country,
        pipeline_status: c.pipeline_status,
        notes: emptyToNull(c.notes),
      });

      if (parsedContact) {
        try {
          await createContact(projectSlug, {
            company_id: created.id,
            full_name: (parsedContact.full_name ?? "").trim(),
            title: emptyToNull(parsedContact.title),
            email: emptyToNull(parsedContact.email),
            phone: emptyToNull(parsedContact.phone),
            linkedin_url: emptyToNull(parsedContact.linkedin_url),
            is_decision_maker: parsedContact.is_decision_maker,
          });
          toast.success("Company created with contact");
        } catch (err) {
          toast.warning(
            "Company created, but the contact couldn't be added. " +
              extractMessage(err),
          );
        }
      } else {
        toast.success("Company created");
      }

      onCreated(created);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractMessage(err));
    }
  }

  const submitting = company.formState.isSubmitting || contact.formState.isSubmitting;

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>New company</DialogTitle>
          <DialogDescription>
            Add a company manually. Optionally include a primary contact and
            internal notes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* ── Company section ─────────────────────────────────── */}
          <SectionTitle>Company</SectionTitle>
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Name"
              error={company.formState.errors.name?.message}
              required
              className="col-span-2"
            >
              <Input
                {...company.register("name")}
                autoFocus
                placeholder="TechFlow AI"
                className="h-10 rounded-lg border-slate-200"
              />
            </Field>
            <Field label="Domain">
              <Input
                {...company.register("domain")}
                placeholder="techflow.ai"
                className="h-10 rounded-lg border-slate-200"
              />
            </Field>
            <Field label="Sector">
              <SectorCombobox
                slug={projectSlug}
                value={company.watch("sector") ?? null}
                onChange={(v) =>
                  company.setValue("sector", v, { shouldDirty: true })
                }
                placeholder="Pick a sector"
                triggerClassName="h-10 rounded-lg border-slate-200"
                clearable
              />
            </Field>
            <Field label="Headcount">
              <Input
                type="number"
                min={0}
                {...company.register("headcount")}
                placeholder="50"
                className="h-10 rounded-lg border-slate-200"
              />
            </Field>
            <Field label="Pipeline status">
              <Select
                value={company.watch("pipeline_status")}
                onValueChange={(v) =>
                  company.setValue("pipeline_status", v, {
                    shouldDirty: true,
                  })
                }
              >
                <SelectTrigger className="h-10 rounded-lg border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pipelineStatuses
                    .slice()
                    .sort((a, b) => a.order - b.order)
                    .map((s) => (
                      <SelectItem key={s.key} value={s.key}>
                        {s.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="City">
              <Input
                {...company.register("city")}
                placeholder="San Francisco"
                className="h-10 rounded-lg border-slate-200"
              />
            </Field>
            <Field label="Country">
              <Select
                value={company.watch("country")}
                onValueChange={(v) =>
                  company.setValue("country", v, { shouldDirty: true })
                }
              >
                <SelectTrigger className="h-10 rounded-lg border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {showStateSelect && (
              <Field label="State">
                <Select
                  value={company.watch("state") ?? NONE}
                  onValueChange={(v) =>
                    company.setValue("state", v === NONE ? null : v, {
                      shouldDirty: true,
                    })
                  }
                >
                  <SelectTrigger className="h-10 rounded-lg border-slate-200">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value={NONE}>—</SelectItem>
                    {US_STATES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </div>

          {/* ── Contact section ─────────────────────────────────── */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-4">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setContactExpanded((v) => !v)}
                className="flex items-center gap-2 text-sm font-medium text-slate-700"
              >
                {contactExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
                <UserPlus className="h-3.5 w-3.5 text-indigo-500" />
                Primary contact
                <span className="text-xs font-normal text-slate-400">
                  Optional
                </span>
              </button>
              <Switch
                checked={contactEnabled}
                onCheckedChange={(v) => {
                  setContactEnabled(v);
                  if (v) setContactExpanded(true);
                }}
              />
            </div>

            {contactEnabled && contactExpanded && (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <Field
                  label="Full name"
                  error={contact.formState.errors.full_name?.message}
                  className="col-span-2"
                >
                  <Input
                    {...contact.register("full_name")}
                    placeholder="Jane Doe"
                    className="h-10 rounded-lg border-slate-200"
                  />
                </Field>
                <Field label="Title">
                  <Input
                    {...contact.register("title")}
                    placeholder="CEO"
                    className="h-10 rounded-lg border-slate-200"
                  />
                </Field>
                <Field label="Email" error={contact.formState.errors.email?.message}>
                  <Input
                    type="email"
                    {...contact.register("email")}
                    placeholder="name@company.com"
                    className="h-10 rounded-lg border-slate-200"
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    type="tel"
                    {...contact.register("phone")}
                    placeholder="+1 415 555 0100"
                    className="h-10 rounded-lg border-slate-200"
                  />
                </Field>
                <Field
                  label="LinkedIn"
                  error={contact.formState.errors.linkedin_url?.message}
                >
                  <Input
                    type="url"
                    {...contact.register("linkedin_url")}
                    placeholder="https://linkedin.com/in/…"
                    className="h-10 rounded-lg border-slate-200"
                  />
                </Field>
                <div className="col-span-2 flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                  <div>
                    <div className="text-sm font-medium text-slate-900">
                      Decision maker
                    </div>
                    <p className="text-xs text-slate-500">
                      Mark as a key contact for sales
                    </p>
                  </div>
                  <Switch
                    checked={contact.watch("is_decision_maker")}
                    onCheckedChange={(v) =>
                      contact.setValue("is_decision_maker", v, {
                        shouldDirty: true,
                      })
                    }
                  />
                </div>
              </div>
            )}
          </div>

          {/* ── Notes section ────────────────────────────────────── */}
          <SectionTitle>Notes</SectionTitle>
          <Textarea
            {...company.register("notes")}
            placeholder="Internal notes about this company…"
            className="min-h-[80px] rounded-lg border-slate-200"
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={submitting}
          >
            {submitting && (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
      {children}
    </h3>
  );
}

function Field({
  label,
  error,
  required,
  className,
  children,
}: {
  label: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
        {required && <span className="ml-1 text-rose-500">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
}

function emptyToNull(v: string | null | undefined): string | null {
  if (v === null || v === undefined) return null;
  const trimmed = v.trim();
  return trimmed === "" ? null : trimmed;
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
  }
  return "Couldn't create the company.";
}
